import Phaser from 'phaser';
import type { MassSystem } from '@/systems/MassSystem';
import type { BrainSystem } from '@/systems/BrainSystem';
import type { SentienceGate } from '@/systems/SentienceGate';
import type { AbilitySystem } from '@/systems/AbilitySystem';

/**
 * Compact stats readout in the top-left. Sentience-gated —
 * levels gradually reveal mass, brain, tier, abilities.
 */
export class StatusStrip {
  private bg: Phaser.GameObjects.Rectangle;
  private tierPulse: Phaser.GameObjects.Arc;
  private massText: Phaser.GameObjects.Text;
  private brainText: Phaser.GameObjects.Text;
  private abilityText: Phaser.GameObjects.Text;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly mass: MassSystem,
    private readonly brain: BrainSystem,
    private readonly gate: SentienceGate,
    private readonly abilities: AbilitySystem,
  ) {
    const mobile = StatusStrip.detectMobile();
    const baseFont = mobile ? '22px' : '16px';
    const smallFont = mobile ? '18px' : '14px';

    this.bg = scene.add
      .rectangle(16, 148, 260, 76, 0x000000, 0.5)
      .setOrigin(0, 0)
      .setScrollFactor(0, 0)
      .setDepth(1000);

    this.tierPulse = scene.add
      .circle(36, 184, 14, 0x6cff6c, 0.6)
      .setStrokeStyle(2, 0x9cff9c, 0.8)
      .setScrollFactor(0, 0)
      .setDepth(1001);

    this.massText = scene.add
      .text(62, 170, '', { fontSize: baseFont, color: '#9cff9c', fontFamily: 'monospace' })
      .setScrollFactor(0, 0)
      .setDepth(1001);

    this.brainText = scene.add
      .text(62, 196, '', { fontSize: smallFont, color: '#b0b0ff', fontFamily: 'monospace' })
      .setScrollFactor(0, 0)
      .setDepth(1001);

    this.abilityText = scene.add
      .text(16, 232, '', {
        fontSize: smallFont,
        color: '#9fdfff',
        fontFamily: 'monospace',
        wordWrap: { width: 260 },
      })
      .setScrollFactor(0, 0)
      .setDepth(1001);

    this.refresh();
  }

  refresh(): void {
    const sentience = this.gate.currentLevel;

    if (sentience >= 1) {
      this.massText.setText(
        `mass ${this.mass.currentMass.toFixed(1)}  ·  tier ${this.mass.currentTier}`,
      );
    } else {
      this.massText.setText('');
    }

    if (sentience >= 2) {
      this.brainText.setText(`brain ${(this.brain.currentValue * 100).toFixed(0)}%`);
    } else {
      this.brainText.setText('');
    }

    if (sentience >= 2) {
      const unlocked = this.abilities.allUnlocked();
      this.abilityText.setText(unlocked.length > 0 ? unlocked.join(' · ') : '');
    } else {
      this.abilityText.setText('');
    }

    this.bg.setVisible(sentience >= 1);
  }

  pulseTier(): void {
    this.scene.tweens.add({
      targets: this.tierPulse,
      scale: { from: 1, to: 1.8 },
      alpha: { from: 1, to: 0.4 },
      duration: 420,
      yoyo: true,
    });
  }

  destroy(): void {
    this.bg.destroy();
    this.tierPulse.destroy();
    this.massText.destroy();
    this.brainText.destroy();
    this.abilityText.destroy();
  }

  private static detectMobile(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(max-width: 900px), (pointer: coarse)').matches ?? false;
  }
}
